import { useState, useEffect } from "react";
import quebecFlag from "/lovable-uploads/b31b2c1d-c00c-4c5e-8899-c2271f1683e3.png";
import texasFlag from "/lovable-uploads/053c6eaf-84ed-4095-9e1e-6717ea773ecc.png";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, CheckCircle, DollarSign, Users, Clock, MapPin } from "lucide-react";

const DemoCreatorRewardsQuebec = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [newCall, setNewCall] = useState<any>(null);

  useEffect(() => {
    if (location.state && location.state.newCall) {
      setNewCall(location.state.newCall);
    }
  }, [location]);

  // Quebec-specific reward distribution based on the $75,000 CAD bounty
  const rewardDistribution = [
    {
      role: "Composer",
      partner: "Quebec Delegation – Houston",
      percentage: 40,
      amount: "$30,000 CAD",
      description: "Strategic coordination and cross-border project management"
    },
    {
      role: "Contributor", 
      partner: "Lion Electric",
      percentage: 35,
      amount: "$26,250 CAD",
      description: "Electric vehicle and battery technology solutions"
    },
    {
      role: "Confirmer",
      partner: "McGill University", 
      percentage: 15,
      amount: "$11,250 CAD",
      description: "Technical validation and environmental impact assessment"
    },
    {
      role: "Creator",
      partner: "Eglantine Pauvarel Moss",
      percentage: 10,
      amount: "$7,500 CAD",
      description: "Project facilitation and Quebec-Houston liaison"
    }
  ];

  const totalBounty = "$75,000 CAD";

  return (
    <div className="min-h-screen bg-white p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/quebec-channel")}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Quebec Channel
          </Button>
          
          <div className="text-center relative">
            {/* Quebec and Texas flags background */}
            <div className="absolute inset-0 opacity-15 flex">
              <div 
                className="w-1/2 bg-cover bg-center bg-no-repeat"
                style={{ 
                  backgroundImage: `url(${quebecFlag})`,
                  backgroundSize: 'contain'
                }}
              ></div>
              <div 
                className="w-1/2 bg-cover bg-center bg-no-repeat"
                style={{ 
                  backgroundImage: `url(${texasFlag})`,
                  backgroundSize: 'contain'
                }}
              ></div>
            </div>
            <img 
              src="/lovable-uploads/bb3f5ac9-6c4b-494d-9a60-86ee7a3feb77.png" 
              alt="iSPAI Logo" 
              className="h-16 mx-auto mb-4" 
            />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Quebec Port Decarbonization - Creator Rewards
            </h1>
            <p className="text-gray-600">
              Solution bounty distribution for Quebec-Houston collaboration
            </p>
          </div>
        </div>

        {/* New Call Confirmation */}
        {newCall && (
          <Card className="mb-6 border-green-200 bg-green-50">
            <CardContent className="p-6">
              <div className="flex items-center mb-4">
                <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                <h2 className="text-lg font-semibold text-green-800">
                  Quebec Call for Solutions Submitted Successfully
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p><strong>Title:</strong> {newCall.title}</p>
                  <p><strong>Area:</strong> {newCall.area}</p>
                </div>
                <div>
                  <p><strong>Type:</strong> {newCall.type}</p>
                  <p><strong>Date:</strong> {newCall.date}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bounty Overview */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <DollarSign className="w-5 h-5 mr-2" />
              Solution Bounty Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{totalBounty}</div>
                <div className="text-sm text-gray-600">Total Solution Bounty</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">4</div>
                <div className="text-sm text-gray-600">Quebec Partners</div>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">18</div>
                <div className="text-sm text-gray-600">Months Duration</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reward Distribution */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Users className="w-5 h-5 mr-2" />
              Quebec Partnership Reward Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {rewardDistribution.map((reward, index) => (
                <div key={index} className="border rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <Badge variant="outline" className="mr-2">
                          {reward.role}
                        </Badge>
                        <h3 className="font-semibold">{reward.partner}</h3>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{reward.description}</p>
                    </div>
                    <div className="text-right ml-4">
                      <div className="text-lg font-bold text-green-600">{reward.amount}</div>
                      <div className="text-sm text-gray-500">{reward.percentage}%</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Project Timeline */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Clock className="w-5 h-5 mr-2" />
              Project Timeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                <div>
                  <strong>Phase 1 (Months 1-6):</strong> Solution design and Quebec regulatory approval
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-yellow-500 rounded-full mr-3"></div>
                <div>
                  <strong>Phase 2 (Months 7-12):</strong> Pilot implementation at Port of Houston
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <div>
                  <strong>Phase 3 (Months 13-18):</strong> Performance validation and scale-up planning
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Cross-Border Impact */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <MapPin className="w-5 h-5 mr-2" />
              Quebec-Houston Collaboration Impact
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold mb-2">Quebec Benefits</h4>
                <ul className="text-sm space-y-1 text-gray-600">
                  <li>• Clean technology export opportunities</li>
                  <li>• Maritime decarbonization expertise</li>
                  <li>• US market access for Quebec companies</li>
                  <li>• Cross-border innovation partnerships</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Houston Benefits</h4>
                <ul className="text-sm space-y-1 text-gray-600">
                  <li>• Advanced clean energy solutions</li>
                  <li>• Port infrastructure modernization</li>
                  <li>• Emission reduction achievements</li>
                  <li>• International sustainability leadership</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Next Steps */}
        <Card>
          <CardHeader>
            <CardTitle>Next Steps</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-blue-800 mb-2">
                  Quebec Delegation Coordination
                </h4>
                <p className="text-sm text-blue-700">
                  The Quebec Delegation – Houston will coordinate with all partners to finalize project scope, 
                  timelines, and regulatory requirements for both Quebec and Texas jurisdictions.
                </p>
              </div>
              <div className="flex justify-center">
                <Button 
                  onClick={() => navigate("/quebec-channel")}
                  className="bg-[#14213d] hover:bg-[#0d1829] text-white px-8 py-2"
                >
                  Return to Quebec Channel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DemoCreatorRewardsQuebec;